import Card from '../../components/ui/Card';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { citiesApi, type CityConfigBundle } from '../../api/cities';
import type { ModuleVisibility, ColumnConfig, DocumentConfig } from '../../api/config';
import { MODULE_LABELS, COLUMN_LABELS, DOCUMENT_ENTITY_LABELS, DOCUMENT_ACTION_LABELS } from '../../config/labels';
import { cityScrapeApi, type ScraperCategory, type ScrapeUrlItem } from '../../api/cityConfig';
import StatusConfigEditor from '../../components/admin/StatusConfigEditor';
import ScraperParameterFields from '../../components/admin/ScraperParameterFields';
import { initialScraperParameters } from '../../utils/scraperParameters';
import CityLetterheadsTab from './CityLetterheadsTab';

const LOGO_ACCEPT = 'image/svg+xml,image/png,image/jpeg,.svg,.png,.jpg,.jpeg';

type Tab =
  | 'stammdaten' | 'zipcodes' | 'scrape-urls' | 'scrape-config'
  | 'status' | 'module' | 'spalten' | 'sektionen' | 'dokumente' | 'briefkoepfe'
  | 'export-import';

const TABS: { key: Tab; label: string }[] = [
  { key: 'stammdaten',    label: 'Stammdaten' },
  { key: 'zipcodes',      label: 'Postleitzahlen' },
  { key: 'scrape-urls',   label: 'Quellen-URLs' },
  { key: 'scrape-config', label: 'Aktualisierung' },
  { key: 'status',        label: 'Status' },
  { key: 'module',        label: 'Module' },
  { key: 'spalten',       label: 'Spalten' },
  { key: 'sektionen',     label: 'Sektionen' },
  { key: 'dokumente',     label: 'Dokumente' },
  { key: 'briefkoepfe',   label: 'Briefköpfe' },
  { key: 'export-import', label: 'Export/Import' },
];

export default function CityDetail() {
  const { cityId } = useParams<{ cityId: string }>();
  const id = Number(cityId);
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('stammdaten');

  const { data: city, isLoading, error } = useQuery({
    queryKey: ['city', id],
    queryFn: () => citiesApi.get(id),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: { name?: string; timezone?: string; is_active?: boolean; default_plz?: string | null }) =>
      citiesApi.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['city', id] });
      queryClient.invalidateQueries({ queryKey: ['cities'] });
    },
  });

  if (isLoading) return <div className="p-6">Lädt…</div>;
  if (error || !city) return <div className="p-6 text-red-600">Stadt nicht gefunden</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-4">
      <h1 className="text-2xl font-semibold">{city.name}</h1>

      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-900'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'stammdaten' && (
        <>
          <LogoCard cityId={id} logoUrl={city.logo_url} />
          <CityFields city={city} onSave={(p) => updateMutation.mutate(p)} saving={updateMutation.isPending} />
        </>
      )}
      {tab === 'zipcodes' && (
        <ZipcodesTab
          cityId={id}
          defaultPlz={city.default_plz}
          onDefaultPlzChange={(plz) => updateMutation.mutate({ default_plz: plz })}
          updatePending={updateMutation.isPending}
          updateError={(updateMutation.error as Error | null)?.message}
        />
      )}
      {tab === 'scrape-urls' && <ScrapeUrlsTab cityId={id} />}
      {tab === 'scrape-config' && <ScrapeConfigTab cityId={id} />}
      {tab === 'status' && <StatusConfigTab cityId={id} />}
      {tab === 'module' && <ModuleVisibilityTab cityId={id} />}
      {tab === 'spalten' && <ColumnConfigTab cityId={id} />}
      {tab === 'sektionen' && <SectionVisibilityTab cityId={id} />}
      {tab === 'dokumente' && <DocumentConfigTab cityId={id} />}
      {tab === 'briefkoepfe' && <CityLetterheadsTab />}
      {tab === 'export-import' && <ConfigExportImportTab cityId={id} cityName={city.name} />}
    </div>
  );
}
// ─── Logo ──────────────────────────────────────────────────────────────────

function LogoCard({ cityId, logoUrl }: { cityId: number; logoUrl: string | null }) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<File | null>(null);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => citiesApi.uploadLogo(cityId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['city', cityId] });
      queryClient.invalidateQueries({ queryKey: ['cities'] });
      setPending(null);
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => citiesApi.deleteLogo(cityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['city', cityId] });
      queryClient.invalidateQueries({ queryKey: ['cities'] });
    },
  });

  // Cache-bust the displayed logo after an upload so the new image is shown
  // (the URL is stable, so the browser would otherwise cache the old file).
  const cacheBustedLogoUrl = logoUrl ? `${logoUrl}?v=${uploadMutation.submittedAt ?? ''}` : null;

  return (
    <Card className="border border-gray-200 p-5 space-y-3">
      <h2 className="font-semibold">Logo</h2>
      <p className="text-sm text-gray-500">
        Wird in der Kopfzeile oben links für alle Nutzer dieser Stadt angezeigt. SVG/PNG/JPG, max. 2&nbsp;MB.
      </p>

      <div className="flex items-center gap-4">
        {cacheBustedLogoUrl ? (
          <img
            src={cacheBustedLogoUrl}
            alt="Logo"
            className="h-14 w-auto max-w-[220px] object-contain border border-gray-200 rounded bg-white p-1"
          />
        ) : (
          <div className="h-14 w-14 border border-dashed border-gray-300 rounded text-gray-300 text-xs flex items-center justify-center">
            kein Logo
          </div>
        )}
        <input
          type="file"
          accept={LOGO_ACCEPT}
          onChange={(e) => setPending(e.target.files?.[0] ?? null)}
          className="text-sm"
        />
      </div>

      {uploadMutation.error && <div className="text-red-600 text-sm">{uploadMutation.error.message}</div>}
      {removeMutation.error && <div className="text-red-600 text-sm">{removeMutation.error.message}</div>}

      <div className="flex gap-2 justify-end">
        {logoUrl && (
          <button
            onClick={() => {
              if (confirm('Logo entfernen?')) removeMutation.mutate();
            }}
            disabled={removeMutation.isPending}
            className="btn btn-outline border-danger-200 text-danger-700 enabled:hover:bg-danger-50 focus-visible:ring-danger-500 px-3 py-1.5"
          >
            {removeMutation.isPending ? 'Entfernen…' : 'Entfernen'}
          </button>
        )}
        <button
          onClick={() => pending && uploadMutation.mutate(pending)}
          disabled={!pending || uploadMutation.isPending}
          className="btn btn-primary px-3 py-1.5"
        >
          {uploadMutation.isPending ? 'Hochladen…' : 'Hochladen'}
        </button>
      </div>
    </Card>
  );
}

// ─── Stammdaten ─────────────────────────────────────────────────────────────

function CityFields({
  city,
  onSave,
  saving,
}: {
  city: { name: string; timezone: string };
  onSave: (p: { name?: string; timezone?: string }) => void;
  saving: boolean;
}) {
  const [name, setName] = useState(city.name);
  const [tz, setTz] = useState(city.timezone);
  const changed = name !== city.name || tz !== city.timezone;

  return (
    <Card className="border border-gray-200 p-5 space-y-3">
      <h2 className="font-semibold">Stammdaten</h2>
      <label className="block">
        <span className="text-sm text-gray-700">Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full px-3 py-1.5 border border-gray-300 rounded" />
      </label>
      <label className="block">
        <span className="text-sm text-gray-700">Zeitzone</span>
        <input value={tz} onChange={(e) => setTz(e.target.value)} className="mt-1 w-full px-3 py-1.5 border border-gray-300 rounded" />
      </label>
      <div className="flex justify-end">
        <button
          disabled={!changed || saving}
          onClick={() => onSave({ name, timezone: tz })}
          className="btn btn-primary px-3 py-1.5"
        >
          {saving ? 'Speichern…' : 'Speichern'}
        </button>
      </div>
    </Card>
  );
}

// ─── Zipcodes ──────────────────────────────────────────────────────────────

function ZipcodesTab({
  cityId,
  defaultPlz,
  onDefaultPlzChange,
  updatePending,
  updateError,
}: {
  cityId: number;
  defaultPlz: string | null;
  onDefaultPlzChange: (plz: string | null) => void;
  updatePending: boolean;
  updateError?: string;
}) {
  const queryClient = useQueryClient();
  const { data: zipcodes } = useQuery({
    queryKey: ['city', cityId, 'zipcodes'],
    queryFn: () => cityScrapeApi.getZipcodes(cityId),
  });

  const [text, setText] = useState<string>('');
  const [initialized, setInitialized] = useState(false);
  // Seed the textarea once the server response arrives. Guarded so the user
  // can subsequently clear the field without it snapping back to the server value.
  useEffect(() => {
    if (zipcodes && !initialized) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Seed the editable text once when async postcode data arrives.
      setText(zipcodes.join('\n'));
      setInitialized(true);
    }
  }, [zipcodes, initialized]);

  const saveMutation = useMutation({
    mutationFn: (list: string[]) => cityScrapeApi.setZipcodes(cityId, list),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['city', cityId, 'zipcodes'] });
      queryClient.invalidateQueries({ queryKey: ['city', cityId] });
    },
  });

  return (
    <div className="space-y-4">
      <Card className="border border-gray-200 p-5 space-y-3">
        <h2 className="font-semibold">Postleitzahlen</h2>
        <p className="text-sm text-gray-500">Eine PLZ pro Zeile. Inserate außerhalb dieser PLZ werden für diese Stadt nicht angezeigt.</p>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={8}
          className="w-full px-3 py-2 border border-gray-300 rounded font-mono text-sm"
        />
        {saveMutation.error && <div className="text-red-600 text-sm">{saveMutation.error.message}</div>}
        {saveMutation.isSuccess && <div className="text-green-700 text-sm">Gespeichert.</div>}
        <div className="flex justify-end">
          <button
            disabled={saveMutation.isPending}
            onClick={() => {
              const list = text.split(/[\s,]+/).map((z) => z.trim()).filter(Boolean);
              saveMutation.mutate(list);
            }}
            className="btn btn-primary px-3 py-1.5"
          >
            {saveMutation.isPending ? 'Speichern…' : 'Speichern'}
          </button>
        </div>
      </Card>

      <Card className="border border-gray-200 p-5 space-y-3">
        <h2 className="font-semibold">Standard-PLZ für Kartenansichten</h2>
        <p className="text-sm text-gray-500">
          Initialer Ladepunkt aller Kartenviews dieser Stadt. Wird beim ersten Speichern der PLZ-Liste
          automatisch auf die erste eingegebene PLZ gesetzt und kann hier überschrieben werden.
        </p>
        <label className="block">
          <span className="text-sm text-gray-700">Standard-PLZ</span>
          <select
            value={defaultPlz ?? ''}
            disabled={updatePending || !zipcodes || zipcodes.length === 0}
            onChange={(e) => onDefaultPlzChange(e.target.value || null)}
            className="mt-1 w-full px-3 py-1.5 border border-gray-300 rounded font-mono text-sm"
          >
            {(zipcodes ?? []).length === 0 && <option value="">– Erst PLZ-Liste speichern –</option>}
            {(zipcodes ?? []).map((z) => (
              <option key={z} value={z}>{z}</option>
            ))}
          </select>
        </label>
        {updateError && <div className="text-red-600 text-sm">{updateError}</div>}
      </Card>
    </div>
  );
}

// ─── Scrape URLs ───────────────────────────────────────────────────────────

function ScrapeUrlsTab({ cityId }: { cityId: number }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: urls } = useQuery({
    queryKey: ['city', cityId, 'scrape-urls'],
    queryFn: () => cityScrapeApi.getScrapeUrls(cityId),
  });
  const { data: categories = [] } = useQuery({
    queryKey: ['scraper-categories', cityId],
    queryFn: () => cityScrapeApi.getScraperCategories(cityId),
  });

  const [draft, setDraft] = useState<ScrapeUrlItem[] | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const current = draft ?? urls ?? [];

  const saveMutation = useMutation({
    mutationFn: (list: ScrapeUrlItem[]) => cityScrapeApi.setScrapeUrls(cityId, list),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['city', cityId, 'scrape-urls'] });
      setDraft(null);
    },
  });
  const createMutation = useMutation({
    mutationFn: (source: ScrapeUrlItem) => cityScrapeApi.createScrapeUrl(cityId, source),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['city', cityId, 'scrape-urls'] });
      setCreateOpen(false);
    },
  });

  const update = (i: number, patch: Partial<ScrapeUrlItem>) => {
    setDraft(current.map((u, idx) => (idx === i ? { ...u, ...patch } : u)));
  };
  const remove = (i: number) => setDraft(current.filter((_, idx) => idx !== i));

  const changeCategory = (i: number, category: string) => {
    const definition = categories.find((item) => item.category === category);
    const parameters = Object.fromEntries(
      (definition?.parameters ?? [])
        .filter((parameter) => parameter.default !== null)
        .map((parameter) => [parameter.key, parameter.default]),
    );
    update(i, { category, parameters, is_active: false });
  };

  return (
    <Card className="border border-gray-200 p-5 space-y-3">
      <h2 className="font-semibold">Datenquellen</h2>
      <p className="text-sm text-gray-500">Konfigurierte Quellen für die regelmäßige Marktaktualisierung.</p>

      <div className="space-y-2">
        {current.map((u, i) => (
          <div key={u.id ?? `new-${i}`} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center border-b border-gray-100 pb-2 last:border-0">
            <input
              value={u.label}
              onChange={(e) => update(i, { label: e.target.value })}
              placeholder="Bezeichnung"
              className="sm:col-span-4 px-2 py-1.5 border border-gray-300 rounded text-sm"
            />
            <select
              value={u.category}
              onChange={(e) => changeCategory(i, e.target.value)}
              className="sm:col-span-3 px-2 py-1.5 border border-gray-300 rounded text-sm bg-white"
            >
              {categories.map((category) => (
                <option key={category.category} value={category.category}>{category.label}</option>
              ))}
              {categories.length === 0 && <option value="hometogo">HomeToGo</option>}
            </select>
            <button
              onClick={() => u.id && navigate(`/admin/cities/${cityId}/scrape-urls/${u.id}`)}
              disabled={!u.id}
              className="btn btn-outline sm:col-span-3 px-2 py-1.5 disabled:opacity-40"
              title="Quellenparameter bearbeiten"
            >
              Einstellungen
            </button>
            <div className="sm:col-span-2 flex items-center justify-end gap-3">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={u.is_active}
                  onChange={(e) => update(i, { is_active: e.target.checked })}
                />
                aktiv
              </label>
              <button
                onClick={() => remove(i)}
                className="text-red-600 hover:text-red-800 text-sm"
                title="Entfernen"
              >
                ✕
              </button>
            </div>
          </div>
        ))}
        {current.length === 0 && <p className="text-sm text-gray-400 italic">Noch keine Datenquellen.</p>}
      </div>

      <div className="flex justify-between">
        <button
          onClick={() => setCreateOpen(true)}
          disabled={categories.length === 0}
          className="btn btn-outline px-3 py-1.5"
        >
          Quelle hinzufügen
        </button>
        <button
          disabled={!draft || saveMutation.isPending}
          onClick={() => draft && saveMutation.mutate(draft)}
          className="btn btn-primary px-3 py-1.5"
        >
          {saveMutation.isPending ? 'Speichern…' : 'Speichern'}
        </button>
      </div>

      {saveMutation.error && <div className="text-red-600 text-sm">{saveMutation.error.message}</div>}
      {createOpen && (
        <CreateScrapeSourceModal
          categories={categories}
          pending={createMutation.isPending}
          error={createMutation.error?.message}
          onClose={() => setCreateOpen(false)}
          onSave={(source) => createMutation.mutate(source)}
        />
      )}
    </Card>
  );
}
function CreateScrapeSourceModal({
  categories,
  pending,
  error,
  onClose,
  onSave,
}: {
  categories: ScraperCategory[];
  pending: boolean;
  error?: string;
  onClose: () => void;
  onSave: (source: ScrapeUrlItem) => void;
}) {
  const initialCategory = categories.find((item) => item.category === 'hometogo') ?? categories[0];
  const [category, setCategory] = useState(initialCategory.category);
  const [label, setLabel] = useState(initialCategory.label);
  const [parameters, setParameters] = useState(
    initialScraperParameters(initialCategory.parameters),
  );
  const selectedCategory = categories.find((item) => item.category === category) ?? initialCategory;

  const changeCategory = (nextCategory: string) => {
    const definition = categories.find((item) => item.category === nextCategory);
    if (!definition) return;
    setCategory(definition.category);
    setLabel(definition.label);
    setParameters(initialScraperParameters(definition.parameters));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-scrape-source-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !pending) onClose();
      }}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSave({
            label: label.trim(),
            category,
            parameters,
            is_active: false,
          });
        }}
        className="w-full max-w-xl max-h-[90vh] overflow-y-auto bg-white border border-gray-200 rounded shadow-xl"
      >
        <header className="flex items-center justify-between gap-4 border-b border-gray-200 px-5 py-4">
          <h2 id="create-scrape-source-title" className="text-lg font-semibold text-gray-900">
            Datenquelle hinzufügen
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            aria-label="Popup schließen"
            title="Schließen"
            className="text-2xl leading-none text-gray-500 hover:text-gray-800 disabled:opacity-50"
          >
            ×
          </button>
        </header>

        <div className="space-y-5 px-5 py-5">
          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-gray-800">Bezeichnung *</span>
            <input
              value={label}
              required
              autoFocus
              onChange={(event) => setLabel(event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded"
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-gray-800">Kategorie *</span>
            <select
              value={category}
              required
              onChange={(event) => changeCategory(event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded bg-white"
            >
              {categories.map((item) => (
                <option key={item.category} value={item.category}>{item.label}</option>
              ))}
            </select>
          </label>

          <ScraperParameterFields
            definitions={selectedCategory.parameters}
            parameters={parameters}
            onChange={(key, value) => setParameters((current) => ({ ...current, [key]: value }))}
          />

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <footer className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            className="btn btn-outline"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={pending}
            className="btn btn-primary"
          >
            {pending ? 'Speichert…' : 'Speichern'}
          </button>
        </footer>
      </form>
    </div>
  );
}

// ─── Scrape Config ─────────────────────────────────────────────────────────

function ScrapeConfigTab({ cityId }: { cityId: number }) {
  const queryClient = useQueryClient();
  const { data: config } = useQuery({
    queryKey: ['city', cityId, 'scrape-config'],
    queryFn: () => cityScrapeApi.getScrapeConfig(cityId),
  });

  const [cron, setCron] = useState('');
  const [detailCron, setDetailCron] = useState('');
  const [detailMonitoringEnabled, setDetailMonitoringEnabled] = useState(true);
  const [duplicateCron, setDuplicateCron] = useState('');
  const [duplicateScanEnabled, setDuplicateScanEnabled] = useState(true);
  const [maxListings, setMaxListings] = useState(0);

  // Seed local state from server config on first arrival
  const [initialized, setInitialized] = useState(false);
  if (config && !initialized) {
    setCron(config.cron_schedule);
    setDetailCron(config.detail_cron_schedule);
    setDetailMonitoringEnabled(config.detail_monitoring_enabled);
    setDuplicateCron(config.duplicate_cron_schedule);
    setDuplicateScanEnabled(config.duplicate_scan_enabled);
    setMaxListings(config.max_listings_per_run);
    setInitialized(true);
  }

  const saveMutation = useMutation({
    mutationFn: () =>
      cityScrapeApi.setScrapeConfig(cityId, {
        platforms: config?.platforms ?? ['hometogo'],
        cron_schedule: cron,
        detail_cron_schedule: detailCron,
        detail_monitoring_enabled: detailMonitoringEnabled,
        duplicate_cron_schedule: duplicateCron,
        duplicate_scan_enabled: duplicateScanEnabled,
        max_listings_per_run: maxListings,
        search_filters: config?.search_filters ?? null,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['city', cityId, 'scrape-config'] }),
  });

  const triggerMutation = useMutation({
    mutationFn: () => cityScrapeApi.triggerScrape(cityId),
  });

  const monitorMutation = useMutation({
    mutationFn: () => cityScrapeApi.triggerMonitor(cityId),
  });

  const duplicateMutation = useMutation({
    mutationFn: () => cityScrapeApi.triggerDuplicateScan(cityId),
  });

  return (
    <Card className="border border-gray-200 p-5 space-y-4">
      <div>
        <h2 className="font-semibold">Aktualisierung</h2>
        <p className="text-sm text-gray-500">Zeitplan und Ausführung der Aktualisierung für diese Stadt.</p>
      </div>

      <label className="block">
        <span className="text-sm text-gray-700">Marktliste – Cron-Ausdruck</span>
        <input
          value={cron}
          onChange={(e) => setCron(e.target.value)}
          placeholder="0 2 * * 1"
          className="mt-1 w-full px-3 py-1.5 border border-gray-300 rounded font-mono text-sm"
        />
        <span className="text-xs text-gray-400">Sucht neue Inserate am Markt. Standard: <code>0 2 * * 1</code> (Montags 02:00).</span>
      </label>

      <div>
        <div className="flex items-center justify-between gap-4">
          <label htmlFor="detail-cron" className="text-sm text-gray-700">
            Inserat-Überwachung – Cron-Ausdruck
          </label>
          <label className="inline-flex items-center gap-2 text-sm whitespace-nowrap">
            <input
              type="checkbox"
              checked={detailMonitoringEnabled}
              onChange={(e) => setDetailMonitoringEnabled(e.target.checked)}
            />
            Aktiv
          </label>
        </div>
        <input
          id="detail-cron"
          value={detailCron}
          onChange={(e) => setDetailCron(e.target.value)}
          placeholder="0 3 * * 1"
          className="mt-1 w-full px-3 py-1.5 border border-gray-300 rounded font-mono text-sm"
        />
        <span className="text-xs text-gray-400">
          Prüft Verfügbarkeit & Online-Status jedes nicht-ausgeblendeten Inserats (über HomeToGo).
          Standard: <code>0 3 * * 1</code> (Montags 03:00). Pro Inserat überschreibbar.
        </span>
      </div>

      <div>
        <div className="flex items-center justify-between gap-4">
          <label htmlFor="duplicate-cron" className="text-sm text-gray-700">
            Duplikatsuche – Cron-Ausdruck
          </label>
          <label className="inline-flex items-center gap-2 text-sm whitespace-nowrap">
            <input
              type="checkbox"
              checked={duplicateScanEnabled}
              onChange={(e) => setDuplicateScanEnabled(e.target.checked)}
            />
            Aktiv
          </label>
        </div>
        <input
          id="duplicate-cron"
          value={duplicateCron}
          onChange={(e) => setDuplicateCron(e.target.value)}
          placeholder="0 4 * * 1"
          className="mt-1 w-full px-3 py-1.5 border border-gray-300 rounded font-mono text-sm"
        />
        <span className="text-xs text-gray-400">
          Vergleicht Inserate verschiedener Plattformen auf mögliche Duplikate.
          Standard: <code>0 4 * * 1</code> (Montags 04:00).
        </span>
      </div>

      <label className="block">
        <span className="text-sm text-gray-700">Max. Inserate pro Lauf (0 = unbegrenzt)</span>
        <input
          type="number"
          min={0}
          value={maxListings}
          onChange={(e) => setMaxListings(Number(e.target.value))}
          className="mt-1 w-full px-3 py-1.5 border border-gray-300 rounded"
        />
      </label>

      {config && (
        <p className="text-xs text-gray-500">
          Letzter Lauf: {config.last_run_at ? new Date(config.last_run_at).toLocaleString('de-DE') : '–'}
          {' · '}
          Nächster geplanter Lauf: {config.next_run_at ? new Date(config.next_run_at).toLocaleString('de-DE') : '–'}
        </p>
      )}

      {saveMutation.error && <div className="text-red-600 text-sm">{saveMutation.error.message}</div>}
      {triggerMutation.error && <div className="text-red-600 text-sm">{triggerMutation.error.message}</div>}
      {triggerMutation.isSuccess && <div className="text-green-700 text-sm">Marktliste-Aktualisierung angestoßen.</div>}
      {monitorMutation.error && <div className="text-red-600 text-sm">{monitorMutation.error.message}</div>}
      {monitorMutation.isSuccess && <div className="text-green-700 text-sm">Inserat-Überwachung angestoßen.</div>}
      {duplicateMutation.error && <div className="text-red-600 text-sm">{duplicateMutation.error.message}</div>}
      {duplicateMutation.isSuccess && (
        <div className="text-green-700 text-sm">
          {duplicateMutation.data.status === 'already_running'
            ? 'Eine Duplikatsuche läuft bereits. Es wurde kein neuer Lauf gestartet.'
            : 'Duplikatsuche angestoßen.'}
        </div>
      )}

      <div className="flex flex-wrap gap-2 justify-end">
        <button
          onClick={() => duplicateMutation.mutate()}
          disabled={duplicateMutation.isPending}
          className="btn btn-outline px-3 py-1.5"
        >
          {duplicateMutation.isPending ? 'Starte…' : 'Duplikate jetzt suchen'}
        </button>
        <button
          onClick={() => monitorMutation.mutate()}
          disabled={monitorMutation.isPending}
          className="btn btn-outline px-3 py-1.5"
        >
          {monitorMutation.isPending ? 'Starte…' : 'Inserate jetzt überwachen'}
        </button>
        <button
          onClick={() => triggerMutation.mutate()}
          disabled={triggerMutation.isPending}
          className="btn btn-outline px-3 py-1.5"
        >
          {triggerMutation.isPending ? 'Starte…' : 'Markt jetzt aktualisieren'}
        </button>
        <button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          className="btn btn-primary px-3 py-1.5"
        >
          {saveMutation.isPending ? 'Speichern…' : 'Speichern'}
        </button>
      </div>
    </Card>
  );
}

// ─── Sektionen (per-city visibility toggles) ───────────────────────────────

function SectionVisibilityTab({ cityId }: { cityId: number }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['city', cityId, 'section-visibility'],
    queryFn: () => citiesApi.getSectionVisibility(cityId),
  });

  const mutation = useMutation({
    mutationFn: (payload: { registrierungsnummer?: boolean; baugebiet?: boolean }) =>
      citiesApi.setSectionVisibility(cityId, { inserat: payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['city', cityId, 'section-visibility'] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
    },
  });

  if (isLoading || !data) return <div>Lädt…</div>;

  const flags = data.inserat;

  const SECTIONS: { key: 'registrierungsnummer' | 'baugebiet'; label: string; hint: string }[] = [
    {
      key: 'registrierungsnummer',
      label: 'Registrierungsnummer',
      hint: 'Feld für die behördliche Registrierungsnummer eines Inserats.',
    },
    {
      key: 'baugebiet',
      label: 'Baugebiet',
      hint: 'Feld für die Zuordnung eines Inserats zu einem Baugebiet.',
    },
  ];

  return (
    <Card className="border border-gray-200 p-5 space-y-4">
      <div>
        <h2 className="font-semibold">Sektionen auf Inseratebene</h2>
        <p className="text-sm text-gray-500 mt-1">
          Diese optionalen Sektionen sind standardmäßig ausgeblendet. Aktivieren Sie sie nur,
          wenn das jeweilige Feature für diese Stadt benötigt wird.
        </p>
      </div>

      <ul className="divide-y divide-gray-200">
        {SECTIONS.map((s) => {
          const enabled = flags[s.key];
          return (
            <li key={s.key} className="py-3 flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-medium text-gray-900">{s.label}</div>
                <div className="text-xs text-gray-500">{s.hint}</div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={enabled}
                disabled={mutation.isPending}
                onClick={() => mutation.mutate({ [s.key]: !enabled })}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors disabled:opacity-50 ${
                  enabled ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${
                    enabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </li>
          );
        })}
      </ul>

      {mutation.error && (
        <div className="text-red-600 text-sm">{(mutation.error as Error).message}</div>
      )}
    </Card>
  );
}


// ─── Toggle row (shared by page/column visibility tabs) ─────────────────────

function ToggleRow({
  label,
  hint,
  enabled,
  disabled,
  onToggle,
}: {
  label: string;
  hint?: string;
  enabled: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  return (
    <li className="py-3 flex items-start justify-between gap-4">
      <div>
        <div className="text-sm font-medium text-gray-900">{label}</div>
        {hint && <div className="text-xs text-gray-500">{hint}</div>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        disabled={disabled}
        onClick={onToggle}
        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors disabled:opacity-50 ${
          enabled ? 'bg-blue-600' : 'bg-gray-200'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${
            enabled ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </li>
  );
}

// ─── Status-Konfiguration (system_admin) ────────────────────────────────────

function StatusConfigTab({ cityId }: { cityId: number }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['city', cityId, 'status-config'],
    queryFn: () => citiesApi.getStatusConfig(cityId),
  });

  if (isLoading || !data) return <div>Lädt…</div>;

  return (
    <Card className="border border-gray-200 p-5 space-y-4">
      <div>
        <h2 className="font-semibold">Status-Konfiguration</h2>
        <p className="text-sm text-gray-500 mt-1">
          Definieren Sie die verfügbaren Status für Anfragen, Inserate und Verfahren.
          Jeder Status kann umbenannt, neu sortiert oder entfernt werden.
        </p>
      </div>
      <StatusConfigEditor
        initialConfig={data}
        onSave={async (config) => {
          await citiesApi.setStatusConfig(cityId, config);
          queryClient.invalidateQueries({ queryKey: ['city', cityId, 'status-config'] });
          queryClient.invalidateQueries({ queryKey: ['status-config'] });
        }}
      />
    </Card>
  );
}

// ─── Module (system_admin) ──────────────────────────────────────────────────

function ModuleVisibilityTab({ cityId }: { cityId: number }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['city', cityId, 'module-visibility'],
    queryFn: () => citiesApi.getModuleVisibility(cityId),
  });

  const mutation = useMutation({
    mutationFn: (modules: ModuleVisibility) => citiesApi.setModuleVisibility(cityId, modules),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['city', cityId, 'module-visibility'] });
      queryClient.invalidateQueries({ queryKey: ['config', 'module-visibility'] });
    },
  });

  if (isLoading || !data) return <div>Lädt…</div>;

  return (
    <Card className="border border-gray-200 p-5 space-y-4">
      <div>
        <h2 className="font-semibold">Module</h2>
        <p className="text-sm text-gray-500 mt-1">
          Aktivieren oder deaktivieren Sie ganze Module. Ein deaktiviertes Modul verschwindet
          überall – Navigation, Übersichten, Querverweise und direkte Aufrufe.
        </p>
      </div>
      <ul className="divide-y divide-gray-200">
        {Object.keys(MODULE_LABELS).map((key) => {
          const enabled = data[key] !== false;
          return (
            <ToggleRow
              key={key}
              label={MODULE_LABELS[key]}
              enabled={enabled}
              disabled={mutation.isPending}
              onToggle={() => mutation.mutate({ [key]: !enabled })}
            />
          );
        })}
      </ul>
      {mutation.error && <div className="text-red-600 text-sm">{(mutation.error as Error).message}</div>}
    </Card>
  );
}

// ─── Spalten der Übersichten (system_admin) ─────────────────────────────────

function ColumnConfigTab({ cityId }: { cityId: number }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['city', cityId, 'column-config'],
    queryFn: () => citiesApi.getColumnConfig(cityId),
  });

  const mutation = useMutation({
    mutationFn: (lists: ColumnConfig) => citiesApi.setColumnConfig(cityId, lists),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['city', cityId, 'column-config'] });
      queryClient.invalidateQueries({ queryKey: ['config', 'column-config'] });
    },
  });

  if (isLoading || !data) return <div>Lädt…</div>;

  return (
    <Card className="border border-gray-200 p-5 space-y-6">
      <div>
        <h2 className="font-semibold">Spalten der Übersichten</h2>
        <p className="text-sm text-gray-500 mt-1">
          Blenden Sie optionale Spalten in den Listen aus. Die Namens-Spalte bleibt immer sichtbar.
        </p>
      </div>
      {Object.entries(COLUMN_LABELS).map(([listKey, group]) => {
        const listFlags = data[listKey] ?? {};
        return (
          <div key={listKey}>
            <h3 className="text-sm font-semibold text-gray-800 mb-1">{group.label}</h3>
            <ul className="divide-y divide-gray-200">
              {Object.entries(group.columns).map(([colKey, colLabel]) => {
                const enabled = listFlags[colKey] !== false;
                return (
                  <ToggleRow
                    key={colKey}
                    label={colLabel}
                    enabled={enabled}
                    disabled={mutation.isPending}
                    onToggle={() => mutation.mutate({ [listKey]: { [colKey]: !enabled } })}
                  />
                );
              })}
            </ul>
          </div>
        );
      })}
      {mutation.error && <div className="text-red-600 text-sm">{(mutation.error as Error).message}</div>}
    </Card>
  );
}

// ─── Dokumente-Konfiguration (system_admin) ─────────────────────────────────

function DocumentConfigTab({ cityId }: { cityId: number }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['city', cityId, 'document-config'],
    queryFn: () => citiesApi.getDocumentConfig(cityId),
  });

  const mutation = useMutation({
    mutationFn: (entities: DocumentConfig) => citiesApi.setDocumentConfig(cityId, entities),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['city', cityId, 'document-config'] });
      queryClient.invalidateQueries({ queryKey: ['config', 'document-config'] });
    },
  });

  if (isLoading || !data) return <div>Lädt…</div>;

  return (
    <Card className="border border-gray-200 p-5 space-y-6">
      <div>
        <h2 className="font-semibold">Dokumente je Entität</h2>
        <p className="text-sm text-gray-500 mt-1">
          Steuern Sie pro Entität, ob der Reiter „Dokumente" sichtbar ist und ob dort Dokumente
          hochgeladen bzw. aus Vorlagen erstellt werden dürfen. Hochladen und Erstellen werden auch
          serverseitig durchgesetzt.
        </p>
      </div>
      {Object.entries(DOCUMENT_ENTITY_LABELS).map(([entity, label]) => {
        const flags = data[entity] ?? { tab: true, upload: true, create: true };
        return (
          <div key={entity}>
            <h3 className="text-sm font-semibold text-gray-800 mb-1">{label}</h3>
            <ul className="divide-y divide-gray-200">
              {(['tab', 'upload', 'create'] as const).map((action) => {
                const enabled = flags[action] !== false;
                return (
                  <ToggleRow
                    key={action}
                    label={DOCUMENT_ACTION_LABELS[action]}
                    enabled={enabled}
                    disabled={mutation.isPending}
                    onToggle={() => mutation.mutate({ [entity]: { [action]: !enabled } } as DocumentConfig)}
                  />
                );
              })}
            </ul>
          </div>
        );
      })}
      {mutation.error && <div className="text-red-600 text-sm">{(mutation.error as Error).message}</div>}
    </Card>
  );
}

// ─── Export / Import (system_admin) ─────────────────────────────────────────

const CONFIG_SECTION_LABELS: Record<string, string> = {
  status_config: 'Status-Konfiguration',
  module_visibility: 'Module',
  column_config: 'Spalten der Übersichten',
  section_visibility: 'Sektionen',
};

function ConfigExportImportTab({ cityId, cityName }: { cityId: number; cityName: string }) {
  const queryClient = useQueryClient();
  const [exportError, setExportError] = useState('');
  const [importError, setImportError] = useState('');
  const [applied, setApplied] = useState<string[] | null>(null);

  const handleExport = async () => {
    setExportError('');
    try {
      const bundle = await citiesApi.exportConfig(cityId);
      const slug = cityName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'stadt';
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fewo-config-${slug}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setExportError('Export fehlgeschlagen.');
    }
  };

  const importMutation = useMutation({
    mutationFn: (bundle: CityConfigBundle) => citiesApi.importConfig(cityId, bundle),
    onSuccess: (res) => {
      setApplied(res.applied);
      setImportError('');
      // Applied config affects several caches — refresh the city + config reads.
      queryClient.invalidateQueries({ queryKey: ['city', cityId] });
      queryClient.invalidateQueries({ queryKey: ['config'] });
    },
    onError: (err) => {
      setApplied(null);
      setImportError((err as Error).message || 'Import fehlgeschlagen.');
    },
  });

  const handleFile = async (file: File | null) => {
    if (!file) return;
    setImportError('');
    setApplied(null);
    let bundle: CityConfigBundle;
    try {
      bundle = JSON.parse(await file.text());
    } catch {
      setImportError('Die Datei ist kein gültiges JSON.');
      return;
    }
    importMutation.mutate(bundle);
  };

  return (
    <Card className="border border-gray-200 p-5 space-y-5">
      <div>
        <h2 className="font-semibold">Konfiguration übertragen</h2>
        <p className="text-sm text-gray-500 mt-1">
          Exportieren Sie die übertragbare Konfiguration (Status, Module, Spalten, Sektionen, Dokumente)
          als Datei und importieren Sie sie in ein anderes Deployment. Stadtspezifische Daten
          (Zuständigkeiten, PLZ, Quellen-URLs, Logo) sind bewusst <strong>nicht</strong> enthalten.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={handleExport}
          className="btn btn-primary px-3 py-1.5"
        >
          Konfiguration exportieren
        </button>
        <label className="px-3 py-1.5 border border-gray-300 rounded cursor-pointer hover:bg-gray-50">
          {importMutation.isPending ? 'Importiere…' : 'Konfiguration importieren…'}
          <input
            type="file"
            accept="application/json,.json"
            className="hidden"
            disabled={importMutation.isPending}
            onChange={(e) => { handleFile(e.target.files?.[0] ?? null); e.target.value = ''; }}
          />
        </label>
      </div>

      {exportError && <div className="text-red-600 text-sm">{exportError}</div>}
      {importError && <div className="text-red-600 text-sm">{importError}</div>}
      {applied && (
        <div className="text-green-700 text-sm">
          Importiert: {applied.length > 0
            ? applied.map((k) => CONFIG_SECTION_LABELS[k] ?? k).join(', ')
            : 'keine Abschnitte (Datei war leer)'}
        </div>
      )}
    </Card>
  );
}
